import { useId, useState } from "react";
import clawEaseLogoMark from "../../assets/clawease-logo-mark-vector.svg";
import { DentalEmailMessage } from "./DentalEmailMessage";
import {
  Check,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Globe2,
  Inbox,
  Mail,
  MessageCircleMore,
  MessagesSquare,
  Phone,
  Plug,
  UserRoundCheck,
  type LucideIcon,
} from "lucide-react";
import {
  dentalConversationChannels,
  filterDentalConversations,
  selectDentalConversation,
  type DentalConversationChannel,
  type DentalConversationEvent,
  type DentalConversationExample,
  type DentalConversationFilter,
  type DentalConversationTone,
} from "./dentalConversationExamples";

const channelIcons: Record<DentalConversationChannel, LucideIcon> = {
  Phone,
  Email: Mail,
  WhatsApp: MessageCircleMore,
  "Web Chat": Globe2,
};

const toneStyles: Record<DentalConversationTone, string> = {
  complete: "border-[#dcece1] bg-[#eef7f0] text-[#397b50]",
  review: "border-[#efe6d5] bg-[#fbf6ed] text-[#8b6a32]",
  handoff: "border-[#dfe9f3] bg-[#eff5fb] text-[#53779b]",
};

const statusColors: Record<DentalConversationTone, string> = {
  complete: "text-[#42905c]",
  review: "text-[#a07c3e]",
  handoff: "text-[#6288ad]",
};

function ConversationStatus({ conversation }: { conversation: DentalConversationExample }) {
  return (
    <span className={`inline-flex shrink-0 items-center rounded-[5px] border px-2 py-1 text-[10.5px] font-medium leading-[14px] ${toneStyles[conversation.tone]}`}>
      {conversation.status}
    </span>
  );
}

function ConversationEvent({ event, initials, emailView = false, initiallyOpen = false }: { event: DentalConversationEvent; initials: string; emailView?: boolean; initiallyOpen?: boolean }) {
  if (event.kind === "action") {
    const ActionIcon = event.tone === "handoff" ? UserRoundCheck : event.tone === "review" ? Clock3 : CheckCircle2;
    return (
      <li className={emailView ? "flex justify-end pl-3" : "flex justify-end px-6"}>
        <details className="group w-fit max-w-[min(100%,400px)] rounded-[7px] border border-[#e4e8ed] bg-white">
          <summary className="flex min-h-7 cursor-pointer list-none items-center gap-2 rounded-[7px] px-2.5 py-1.5 text-[11px] font-medium leading-4 text-[#495362] outline-none transition-colors hover:bg-[#fafbfc] focus-visible:ring-1 focus-visible:ring-[#8cafd3] motion-reduce:transition-none [&::-webkit-details-marker]:hidden">
            <ActionIcon aria-hidden="true" strokeWidth={1.75} className={`h-3.5 w-3.5 shrink-0 ${statusColors[event.tone]}`} />
            <span className="min-w-0 flex-1">{event.title}</span>
            <ChevronDown aria-hidden="true" className="ml-1 h-3 w-3 shrink-0 -rotate-90 text-[#94a0af] transition-transform group-open:rotate-0 motion-reduce:transition-none" />
          </summary>
          <ul className="space-y-1 border-t border-[#edf0f3] px-3 py-2.5 text-[11px] leading-[17px] text-[#687687]">
            {event.details.map((detail) => <li key={detail} className="flex gap-1.5"><Check aria-hidden="true" className="mt-1 h-2.5 w-2.5 shrink-0 text-[#94a3b8]" /><span>{detail}</span></li>)}
          </ul>
        </details>
      </li>
    );
  }

  if (emailView && event.email) {
    return <DentalEmailMessage email={event.email} text={event.text} speaker={event.speaker} initiallyOpen={initiallyOpen} />;
  }

  const isPatient = event.speaker === "patient";
  return (
    <li className={`flex items-start gap-2 ${isPatient ? "pr-2" : "flex-row-reverse pl-2"}`}>
      <span aria-hidden="true" className="mt-0.5 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border border-[#e0e5eb] bg-white text-[9.5px] font-medium text-[#637080]">
        {isPatient ? initials : <img src={clawEaseLogoMark} alt="" width={17} height={17} className="h-[17px] w-[17px] object-contain" />}
      </span>
      <div className={`min-w-0 max-w-[min(88%,400px)] break-words rounded-[10px] px-3 py-2 text-[12px] leading-[1.6] text-[#3f4b5a] ${isPatient ? "rounded-tl-[3px] bg-[#edf4fc]" : "rounded-tr-[3px] bg-[#f4f5f6]"}`}>
        <span className="sr-only">{isPatient ? "Patient" : "ClawEase"}: </span>
        {event.text}
      </div>
    </li>
  );
}

function ConversationDetail({ conversation, panelId }: { conversation: DentalConversationExample; panelId: string }) {
  const OutcomeIcon = conversation.tone === "review" ? Clock3 : conversation.tone === "handoff" ? UserRoundCheck : CheckCircle2;
  const isEmail = conversation.channel === "Email";
  const emailMessages = conversation.events.filter((event) => event.kind === "message" && event.email);
  const firstEmail = emailMessages[0];
  const latestEmail = emailMessages[emailMessages.length - 1];
  const emailSubject = firstEmail?.kind === "message" ? firstEmail.email?.subject : undefined;
  return (
    <section id={panelId} aria-label={`${conversation.patient}: ${conversation.subject}`} className="flex min-w-0 flex-col bg-white">
      <div className="flex min-h-[52px] flex-wrap items-center justify-between gap-x-2 gap-y-1 border-b border-[#e8ebef] px-4 py-2">
        <div className="min-w-0">
          <div className="min-w-0">
            <h3 className="text-[12px] font-semibold leading-4 tracking-[-.015em] text-[#29313f]">{isEmail ? emailSubject ?? conversation.subject : conversation.patient}</h3>
            <p className="mt-0.5 text-[10.5px] leading-[15px] text-[#788392]">{isEmail ? `${conversation.patient} · Email · ${emailMessages.length} messages` : <>{conversation.channel}<span aria-hidden="true" className="mx-1.5">·</span>{conversation.topic}{conversation.channel === "Phone" ? " · Transcript" : ""}</>}</p>
          </div>
        </div>
        <ConversationStatus conversation={conversation} />
      </div>

      <div
        key={conversation.id}
        role="region"
        aria-label={`${conversation.patient}'s ${isEmail ? "email thread" : "full conversation"}`}
        tabIndex={0}
        className="h-[360px] overflow-y-auto px-4 py-3 outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-[#93b7df] [scrollbar-color:#cbd5e1_transparent] [scrollbar-width:thin]"
      >
        <ol className={isEmail ? "mx-auto max-w-[512px] space-y-2" : "mx-auto max-w-[512px] space-y-2.5"}>
          {conversation.events.map((event, index) => <ConversationEvent key={index} event={event} initials={conversation.initials} emailView={isEmail} initiallyOpen={isEmail && event === latestEmail} />)}
        </ol>
        <p className="pt-3 text-center text-[10.5px] text-[#8994a2]">{isEmail ? "End of thread" : "End of conversation"}</p>
      </div>

      <div className="mt-auto flex min-h-10 flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-[#e8ebef] bg-[#fdfefe] px-4 py-2">
        <p className="flex items-center gap-2 text-[11px] font-semibold leading-4 text-[#3b4756]">
          <OutcomeIcon aria-hidden="true" strokeWidth={1.75} className={`h-3.5 w-3.5 shrink-0 ${statusColors[conversation.tone]}`} />
          {conversation.outcome.label}
        </p>
        <span className="text-[10.5px] leading-4 text-[#748092]">{conversation.outcome.meta}</span>
        <span className="sr-only">{conversation.outcome.detail}</span>
      </div>
    </section>
  );
}

export function DentalConversationDashboard() {
  const instanceId = useId();
  const panelId = `${instanceId}-conversation`;
  const headingId = `${instanceId}-heading`;
  const [channel, setChannel] = useState<DentalConversationFilter>("All channels");
  const [selectedId, setSelectedId] = useState("new-patient");
  const conversations = filterDentalConversations(channel);
  const selected = selectDentalConversation(selectedId, channel);

  function changeChannel(nextChannel: DentalConversationFilter) {
    setSelectedId(selectDentalConversation(selectedId, nextChannel).id);
    setChannel(nextChannel);
  }

  return (
    <section id="conversation-dashboard" aria-labelledby={headingId} className="scroll-mt-24 bg-[#fbfaf6] px-5 py-8 sm:px-10 sm:py-10 lg:px-12">
      <div className="dental-content relative">
        <header className="text-center">
          <h2 id={headingId} className="mx-auto max-w-[920px] text-balance text-[30px] font-extrabold leading-[1.12] tracking-[-.048em] text-[#172033] sm:text-[38px]">
            Every patient conversation. One clear view.
          </h2>
        </header>
        <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(280px,1fr)_minmax(0,720px)] lg:items-center">
        <div className="min-w-0 lg:pb-5">
          <h3 className="max-w-[350px] text-[23px] font-extrabold leading-[1.2] tracking-[-.035em] text-[#172033]">
            Stay close to every patient.
          </h3>
          <p className="mt-4 max-w-[350px] text-[14px] leading-[1.85] text-[#667280]">
            Keep every patient conversation in one place, from phone, email, WhatsApp and web chat to your own custom channels. See what was said, what got done, and who needs to follow up.
          </p>
          <ul className="mt-7 space-y-4 text-[12.5px] leading-5 text-[#596575]">
            <li className="flex items-center gap-3"><MessageCircleMore aria-hidden="true" strokeWidth={1.5} className="h-5 w-5 shrink-0 text-[#657386]" />Full conversation history</li>
            <li className="flex items-center gap-3"><Inbox aria-hidden="true" strokeWidth={1.5} className="h-5 w-5 shrink-0 text-[#657386]" />Easy conversation management</li>
            <li className="flex items-center gap-3"><Globe2 aria-hidden="true" strokeWidth={1.5} className="h-5 w-5 shrink-0 text-[#657386]" />Reach patients across every channel</li>
          </ul>
          <ul aria-label="Patient channels" className="mt-8 grid w-fit grid-cols-[max-content_max-content_max-content] items-center gap-x-4 gap-y-3 text-[10.5px] text-[#6f7a87] lg:mt-10">
            {dentalConversationChannels.map((name) => {
              const ChannelIcon = channelIcons[name];
              return <li key={name} className="flex items-center gap-1.5 whitespace-nowrap"><ChannelIcon aria-hidden="true" strokeWidth={1.5} className="h-3.5 w-3.5" />{name}</li>;
            })}
            <li className="col-span-2 flex items-center gap-1.5 whitespace-nowrap"><Plug aria-hidden="true" strokeWidth={1.5} className="h-3.5 w-3.5" />Custom channels</li>
          </ul>
        </div>

        <div className="min-w-0 w-full max-w-[720px] justify-self-center lg:justify-self-end">
          <div className="overflow-hidden rounded-[14px] border border-[#dfe4e9] bg-white shadow-[0_6px_20px_-7px_rgba(26,39,56,.10),0_1px_2px_rgba(26,39,56,.025)]">
            <div className="flex min-h-11 flex-wrap items-center justify-between gap-2 border-b border-[#e6e9ee] bg-white px-4 py-1.5">
              <div className="flex items-center gap-2">
                <MessagesSquare aria-hidden="true" strokeWidth={1.5} className="h-[18px] w-[18px] text-[#4e5c6e]" />
                <span className="text-[13px] font-semibold tracking-[-.02em] text-[#29313f]">Conversations</span>
              </div>
            </div>

            <div className="grid sm:grid-cols-[180px_minmax(0,1fr)]">
              <aside aria-label="Choose a dental conversation" className="min-w-0 border-b border-[#e6e9ee] bg-[#f9fafb] sm:border-b-0 sm:border-r">
                <div className="flex min-h-[52px] items-center border-b border-[#e8ebef] px-3 py-2">
                  <label className="relative w-full">
                    <span className="sr-only">Filter conversations by channel</span>
                    <select
                      value={channel}
                      onChange={(event) => changeChannel(event.target.value as DentalConversationFilter)}
                      className="block min-h-8 w-full cursor-pointer appearance-none rounded-[6px] border border-[#e1e6ec] bg-[#fbfcfd] py-1.5 pl-2.5 pr-6 text-[11.5px] font-medium leading-4 text-[#546172] outline-none transition-colors hover:border-[#b9c9da] hover:bg-[#f5f8fb] focus-visible:border-[#648ab2] focus-visible:bg-[#edf4fc] motion-reduce:transition-none"
                    >
                      <option value="All channels">All channels</option>
                      {dentalConversationChannels.map((name) => <option key={name} value={name}>{name}</option>)}
                    </select>
                    <ChevronDown aria-hidden="true" strokeWidth={1.5} className="pointer-events-none absolute right-2 top-1/2 h-2.5 w-2.5 -translate-y-1/2 text-[#8a97a6]" />
                  </label>
                </div>
                <ul className="flex snap-x snap-mandatory overflow-x-auto sm:block sm:overflow-visible">
                  {conversations.map((conversation) => {
                    const ChannelIcon = channelIcons[conversation.channel];
                    const isSelected = selected.id === conversation.id;
                    return (
                      <li key={conversation.id} className="w-[184px] shrink-0 snap-start border-r border-[#e8ebef] last:border-r-0 sm:w-auto sm:border-b sm:border-r-0 sm:last:border-b-0">
                        <button
                          type="button"
                          aria-pressed={isSelected}
                          aria-controls={panelId}
                          onClick={() => setSelectedId(conversation.id)}
                          className={`relative flex min-h-[74px] w-full items-start gap-2 border-l-2 px-3 py-2.5 text-left outline-none transition-colors focus-visible:z-10 focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-[#8cafd3] motion-reduce:transition-none ${isSelected ? "border-l-[#8cafd3] bg-[#f0f6fc]" : "border-l-transparent hover:bg-[#f1f4f7]"}`}
                        >
                          <span aria-hidden="true" className="mt-0.5 flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full border border-[#dde3ea] bg-white text-[10px] font-medium text-[#657183]">{conversation.initials}</span>
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center justify-between gap-1.5">
                              <span className="text-[11.5px] font-semibold leading-4 tracking-[-.015em] text-[#354152]">{conversation.patient}</span>
                              {isSelected ? <span aria-hidden="true" className={`h-[5px] w-[5px] shrink-0 rounded-full bg-current ${statusColors[conversation.tone]}`} /> : null}
                            </span>
                            <span className="mt-0.5 block text-[10.5px] leading-4 text-[#718091]">{conversation.topic}</span>
                            <span className="mt-1 flex items-center gap-1 text-[10.5px] leading-4 text-[#7b8796]"><ChannelIcon aria-hidden="true" strokeWidth={1.5} className="h-3 w-3 shrink-0" />{conversation.channel}</span>
                            <span className="sr-only">{conversation.preview} Status: {conversation.status}.</span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </aside>

              <ConversationDetail key={selected.id} conversation={selected} panelId={panelId} />
            </div>
          </div>
        </div>
        </div>
      </div>
      <p className="sr-only" aria-live="polite" aria-atomic="true">Showing {selected.patient}: {selected.subject}. {selected.channel}. {selected.status}.</p>
    </section>
  );
}
